'use client'

import { useEffect, useRef, useState } from "react"
import { storyblokEditable } from "@storyblok/react"
import { ContactFormStoryblok, ContactFormFieldStoryblok, ContactPersonStoryblok } from "@/component-types-sb"

interface ContactFormProps {
  blok: ContactFormStoryblok
}

export default function ContactForm({ blok }: ContactFormProps) {
  const [form, setForm] = useState<Record<string, string>>({})
  const [submitted, setSubmitted] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [missing, setMissing] = useState<string[]>([])
  // When the form appeared and an off-screen field only scripts fill in — the
  // route answers both like a success and sends nothing (app/api/contact).
  const renderedAt = useRef(0)
  const honeypot = useRef<HTMLInputElement>(null)
  useEffect(() => {
    renderedAt.current = Date.now()
  }, [])

  const fields = blok.form_fields || []

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const requiredFields = fields.filter(f => f.required).map(f => f.name).filter((name): name is string => name !== undefined)
    const missingFields = requiredFields.filter(name => !form[name]?.trim())
    setMissing(missingFields)
    if (missingFields.length > 0) return

    // Thank the visitor only once the mail is out. Until 2026-10 this ignored
    // the answer — a 404, the route did not exist — and thanked everyone.
    setSending(true)
    setError(null)
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          website_url: honeypot.current?.value ?? "",
          form_fill_ms: Date.now() - renderedAt.current,
        }),
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || "Die Nachricht konnte nicht gesendet werden.")
      setSubmitted(true)
    } catch (err) {
      const reason = err instanceof Error ? err.message : "Die Nachricht konnte nicht gesendet werden."
      setError(blok.address_email ? `${reason} Bitte schreiben Sie uns direkt an ${blok.address_email}.` : reason)
    } finally {
      setSending(false)
    }
  }

  return (
    <section {...storyblokEditable(blok)} className="contactForm max-w-[1200px] mx-auto p-5 md:p-10 gap-10">
      {/* Address & Contact Persons */}
      <div>
        {blok.heading && <h2 className="mb-6">{blok.heading}</h2>}
        {(blok.address_name || blok.address_street || blok.address_zipcity || blok.address_tel || blok.address_fax || blok.address_email) && (
        <address className="not-italic mb-6">
          {blok.address_name && <h3>{blok.address_name}</h3>}
          {blok.address_street && <div>{blok.address_street}</div>}
          {blok.address_zipcity && <div>{blok.address_zipcity}</div>}
          {blok.address_tel && (
                <div>
                Tel: <a href={`tel:${blok.address_tel.replace(/\s+/g, '')}`}>{blok.address_tel}</a>
                </div>
            )}
            {blok.address_fax && (
                <div>
                Fax: <a href={`tel:${blok.address_fax.replace(/\s+/g, '')}`}>{blok.address_fax}</a>
                </div>
            )}
          {blok.address_email && <div><a href={`mailto:${blok.address_email}`}>{blok.address_email}</a></div>}
        </address>
        )}
        {(blok.contact_persons?.length ?? 0) > 0 && (
          <div className="mb-6">
            <ul>
              {blok.contact_persons!.map((person: ContactPersonStoryblok) => (
                <li key={person._uid} className="mb-2">
                  <p className="font-bold">{person.title}</p>
                  <p>{person.name}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Contact Form */}
      <div>
        {blok.form_heading && <p className="font-bold mb-4">{blok.form_heading}</p>}
        {submitted ? (
          <div className="p-4 bg-green-100 border border-green-400 rounded">Vielen Dank für Ihre Nachricht!</div>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit} noValidate>
            {fields.map((field: ContactFormFieldStoryblok) => (
              <div key={field._uid} className="flex flex-col gap-1">
                <label htmlFor={field.name} className="font-medium">
                  {field.label}
                  {field.required && <span className="ml-2 bg-primary text-background align-middle px-[6px] py-[4px] rounded-full text-[12px]">Erforderlich</span>}
                </label>
                {field.type === "textarea" ? (
                  <textarea
                    id={field.name}
                    name={field.name}
                    required={field.required}
                    className={`border rounded p-2 ${missing.includes(field.name ?? '') ? "border-red-500" : "border-gray-300"}`}
                    value={form[field.name ?? ''] || ""}
                    onChange={handleChange}
                  />
                ) : (
                  <input
                    id={field.name}
                    name={field.name}
                    type={field.type}
                    required={field.required}
                    className={`border rounded p-2 ${missing.includes(field.name ?? '') ? "border-red-500" : "border-gray-300"}`}
                    value={form[field.name ?? ''] || ""}
                    onChange={handleChange}
                  />
                )}
                {missing.includes(field.name ?? '') && (
                  <span className="text-xs text-red-600">Bitte ausfüllen</span>
                )}
              </div>
            ))}
            {/* Off-screen, for scripts only: a person never sees or fills it. */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
              <label htmlFor="website_url">Website</label>
              <input ref={honeypot} id="website_url" name="website_url" type="text" tabIndex={-1} autoComplete="off" defaultValue="" />
            </div>
            {error && (
              <div role="alert" className="p-4 bg-red-100 border border-red-400 rounded">
                {error}
              </div>
            )}
            <button type="submit" disabled={sending} className="bg-primary text-white px-6 py-2 rounded font-bold hover:bg-primary/80 disabled:opacity-60 mb-10">
              {sending ? "Wird gesendet …" : "Absenden"}
            </button>
          </form>
        )}
      </div>
    </section>
  )
}